import { useState, type FormEvent } from "react";
import { Link, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import { ControlActivityEntry, Invitation, Workspace } from "@workcore/schemas";
import { api, checkedWrite, get } from "../api";
import { Breadcrumbs, ErrorBox, Field, InviteSent, PageHeader, Panel, RowMenu, SidePanel, StatusBadge, When, when } from "../ui";
import { WorkspaceActions } from "./Workspaces";
import { confirmAction } from "../confirm";

export const ACTIONS: Record<string, string> = {
  workspace_requested: "Requested",
  provisioning_started: "Provisioning started",
  provisioned: "Provisioned",
  provision_failed: "Provisioning failed",
  retried: "Retry requested",
  archived: "Archived",
  unarchived: "Unarchived",
  invitation_created: "Invitation sent",
  invitation_revoked: "Invitation revoked",
  invitation_accepted: "Invitation accepted",
  operator_granted: "Operator granted",
  operator_revoked: "Operator revoked",
};

export function describe(e: ControlActivityEntry): string {
  const d = e.details as Record<string, unknown>;
  if (e.action.startsWith("invitation")) return `${String(d.email ?? "")}${d.systemAdmin ? " (system admin)" : ""}`;
  if (e.action.startsWith("operator")) return String(d.email ?? "");
  if (e.action === "provision_failed") return String(d.error ?? "");
  if (e.action === "provisioned") return `schema v${String(d.schemaVersion)}${Array.isArray(d.starterTemplates) && d.starterTemplates.length ? `; loaded ${d.starterTemplates.join(", ")}` : ""}`;
  return "";
}

function Invitations({ slug }: { slug: string }) {
  const queries = useQueryClient();
  const key = ["invitations", slug];
  const list = useQuery({ queryKey: key, queryFn: async () => z.array(Invitation).parse(await get(`/v1/admin/workspaces/${slug}/invitations`, { all: "true" })) });
  const [email, setEmail] = useState("");
  const [systemAdmin, setSystemAdmin] = useState(true);
  const [adding, setAdding] = useState(false);
  const [sent, setSent] = useState<string>();
  const refresh = () => {
    void queries.invalidateQueries({ queryKey: key });
    void queries.invalidateQueries({ queryKey: ["activity", slug] });
  };
  const invite = useMutation({
    mutationFn: () => checkedWrite("POST", `/v1/admin/workspaces/${slug}/invitations`, { email, systemAdmin }),
    onSuccess: () => {
      setSent(email);
      setEmail("");
      setAdding(false);
      refresh();
    },
  });
  const revoke = useMutation({ mutationFn: (id: string) => api("DELETE", `/v1/admin/workspaces/${slug}/invitations/${id}`), onSuccess: refresh });
  return (
    <Panel title="Invitations" plain actions={<button className="secondary" onClick={() => setAdding(true)}>Invite someone</button>}>
      {sent && <InviteSent email={sent} onDone={() => setSent(undefined)} />}
      <SidePanel title="Invite to this workspace" open={adding} onClose={() => setAdding(false)} onSubmit={() => invite.mutate()} submitLabel="Send invitation" busy={invite.isPending} error={invite.error}>
        <p className="muted small">They join when they first sign in with this email.</p>
        <Field label="Email">
          <input type="email" required value={email} autoFocus onChange={(e) => setEmail(e.target.value)} placeholder="name@example.com" />
        </Field>
        <label className="check">
          <input type="checkbox" checked={systemAdmin} onChange={(e) => setSystemAdmin(e.target.checked)} /> Make them a system admin
        </label>
      </SidePanel>
      <ErrorBox error={revoke.error ?? list.error} />
      <table className="grid">
        <thead>
          <tr>
            <th>Email</th>
            <th>Role</th>
            <th>Status</th>
            <th>Expires</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {list.data?.map((i) => (
            <tr key={i.id}>
              <td>{i.email}</td>
              <td>{i.systemAdmin ? `${i.role} + system admin` : i.role}</td>
              <td>
                <StatusBadge status={i.status} />
              </td>
              <td><When at={i.expiresAt} /></td>
              <td className="row-actions">{i.status === "open" && <RowMenu label={`Actions for ${i.email}`} items={[{ label: "Revoke…", danger: true, onSelect: () => void confirmAction({ title: `Revoke the invitation for ${i.email}?`, body: "They can no longer join with it. You can invite them again.", action: "Revoke", check: () => api("DELETE", `/v1/admin/workspaces/${slug}/invitations/${i.id}`, { dryRun: true }) }).then((ok) => ok && revoke.mutate(i.id)) }]} />}</td>
            </tr>
          ))}
          {list.data?.length === 0 && (
            <tr>
              <td colSpan={5} className="muted">
                No invitations yet.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </Panel>
  );
}

export function WorkspaceDetailPage() {
  const { slug = "" } = useParams();
  const ws = useQuery({
    queryKey: ["workspace", slug],
    queryFn: async () => Workspace.parse(await get(`/v1/admin/workspaces/${slug}`)),
    refetchInterval: (q) => (q.state.data && ["requested", "provisioning"].includes(q.state.data.status) ? 2000 : false),
  });
  const activity = useQuery({
    queryKey: ["activity", slug],
    queryFn: async () => z.array(ControlActivityEntry).parse(await get("/v1/admin/activity", { workspace: slug, limit: 100 })),
    refetchInterval: ws.data && ["requested", "provisioning"].includes(ws.data.status) ? 2000 : false,
  });
  const w = ws.data;
  return (
    <>
      <ErrorBox error={ws.error} />
      {w && (
        <>
          <PageHeader back={<Breadcrumbs items={[{ label: "Workspaces", to: "/platform/workspaces" }, { label: w.name }]} />} title={<>{w.name} <StatusBadge status={w.status} /></>} actions={<WorkspaceActions w={w} />} />
          <dl className="facts">
            <dt>Slug</dt>
            <dd>
              <code>{w.slug}</code>
            </dd>
            <dt>Database</dt>
            <dd>
              <code>{w.dbName}</code> on {w.dbHost}, schema v{w.schemaVersion}
            </dd>
            <dt>Requested</dt>
            <dd>{when(w.requestedAt)}</dd>
            <dt>Starter workflows</dt>
            <dd>{w.starterTemplates.length ? w.starterTemplates.join(", ") : "none"}</dd>
            {w.lastError && (
              <>
                <dt>Last error</dt>
                <dd className="err">
                  attempt {w.attempts}: {w.lastError}
                </dd>
              </>
            )}
          </dl>
          {w.status !== "archived" && <Invitations slug={slug} />}
          <Panel title="Activity">
            <ErrorBox error={activity.error} />
            <ol className="timeline">
              {activity.data?.map((e) => (
                <li key={e.id}>
                  <When at={e.at} /> <strong>{ACTIONS[e.action] ?? e.action}</strong> <span className="muted">{describe(e)}</span>
                  {e.actorIdentityId === null && <span className="tag">system</span>}
                </li>
              ))}
            </ol>
          </Panel>
        </>
      )}
    </>
  );
}
